// Quilting thread hub: rail dots and prev/next, Marqo default sort, and keeping ?view= on pagination.
// Loaded by snippets/thread-interactions.liquid from every rail section and the product grid.
(() => {
  // Each hub section adds a tag for this file, so it can run more than once; later runs only bind
  // anything that has rendered since.
  if (window.msqcThreadHub) {
    window.msqcThreadHub.bindAll();
    return;
  }
  function bindRails() {
    document.querySelectorAll('[data-thread-rail]').forEach((rail) => {
      if (rail.dataset.threadBound) return;
      rail.dataset.threadBound = 'true';
      const dots = document.querySelector(`[data-thread-dots="${rail.id}"]`);
      if (!dots) return;
      const update = () => {
        let active = 0;
        const first = rail.children[0];
        if (first) {
          const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
          active = Math.round(rail.scrollLeft / (first.getBoundingClientRect().width + gap));
        }
        // a rail stops scrolling before its last items reach the start, so treat the end as the last dot
        // (only once it has scrolled: a rail that doesn't overflow yet is also "at the end")
        const maxScroll = rail.scrollWidth - rail.clientWidth;
        if (maxScroll > 2 && rail.scrollLeft > 0 && rail.scrollLeft >= maxScroll - 2) active = dots.children.length - 1;
        active = Math.min(active, dots.children.length - 1);
        [...dots.children].forEach((dot, index) => {
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      };
      rail.addEventListener('scroll', update, { passive: true });
      // overflow changes as images and product cards load and when the viewport resizes
      if (window.ResizeObserver) new ResizeObserver(update).observe(rail);
      update();
    });
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-thread-scroll], [data-thread-slide]');
    if (!button) return;
    const rail = document.getElementById(button.getAttribute('aria-controls'));
    if (!rail) return;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    if (button.hasAttribute('data-thread-slide')) {
      const child = rail.children[Number(button.dataset.threadSlide)];
      if (child)
        rail.scrollTo({
          left: child.offsetLeft - rail.children[0].offsetLeft,
          behavior,
        });
    } else {
      rail.scrollBy({
        left: rail.clientWidth * Number(button.dataset.threadScroll),
        behavior,
      });
    }
  });
  // Start-card add to cart: keep the customer on the hub. addToCart (assets/application.js) opens the
  // cart drawer or a notification; without it the form posts to /cart as normal.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-thread-cart]');
    if (!form || typeof window.addToCart !== 'function') return;
    event.preventDefault();
    let properties = {};
    try {
      properties = JSON.parse(form.querySelector('[data-thread-cart-properties]')?.value || '{}');
    } catch (error) {
      console.error(error);
    }
    window.addToCart(Number(form.elements.id.value), Number(form.elements.quantity.value) || 1, properties);
  });
  // Marqo pages its results in place and then scrolls the window to the top (window.scrollTo({ top: 0 })),
  // sometimes more than once per page change, with no setting to turn that off. From a pagination click
  // until just after the new results arrive, send those scrolls to the top of the product grid instead,
  // so the page moves once rather than up and back down.
  const nativeScrollTo = window.scrollTo.bind(window);
  let redirectUntil = 0;
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('.thread-products nav[aria-label="Pagination"]')) redirectUntil = Date.now() + 5000;
    },
    true
  );
  document.addEventListener('marqo:search.results', () => {
    if (Date.now() < redirectUntil) redirectUntil = Date.now() + 1000;
  });
  window.scrollTo = function (options, y) {
    const grid = document.querySelector('.thread-products');
    const toTop = options && typeof options === 'object' ? options.top === 0 : options === 0 && y === 0;
    if (grid && toTop && Date.now() < redirectUntil) {
      const top = grid.getBoundingClientRect().top + window.scrollY;
      return nativeScrollTo({ ...(typeof options === 'object' ? options : {}), top });
    }
    return nativeScrollTo(...arguments);
  };
  function bindDefaultSort() {
    const grid = document.querySelector('.thread-products');
    if (!grid || grid.dataset.threadSortBound) return;
    grid.dataset.threadSortBound = 'true';
    if (new URL(location.href).searchParams.has('sort_by')) return;
    const apply = () => {
      const select = grid.querySelector('#marqo-sort-select');
      const option = select && [...select.options].find((option) => option.textContent.trim() === 'Best Sellers');
      if (!option) return false;
      if (select.value !== option.value) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    };
    if (apply()) return;
    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(grid, { childList: true, subtree: true });
    // stop looking if the app never renders a Best Sellers option (renamed or removed)
    setTimeout(() => observer.disconnect(), 10000);
  }
  function bindPagination() {
    const grid = document.querySelector('.thread-products');
    if (!grid || grid.dataset.threadBound) return;
    grid.dataset.threadBound = 'true';
    const retainState = () => {
      // The app can recreate its hidden H1 after rendering; retain one page H1.
      const heading = grid.querySelector('h1.marqo-page-heading');
      if (heading) {
        const replacement = document.createElement('h2');
        for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
        replacement.replaceChildren(...heading.childNodes);
        heading.replaceWith(replacement);
      }
      const current = new URL(location.href);
      if (!current.searchParams.has('view')) return;
      grid.querySelectorAll('nav[aria-label="Pagination"] a[href]').forEach((link) => {
        const destination = new URL(link.href, location.href);
        if (destination.origin !== current.origin || destination.pathname !== current.pathname) return;
        const next = new URL(current.href);
        next.hash = '';
        const page = destination.searchParams.get('page');
        if (page) next.searchParams.set('page', page);
        else next.searchParams.delete('page');
        if (link.href !== next.href) link.href = next.href;
      });
    };
    retainState();
    // the app re-renders often (images, ratings, filters); run once per frame rather than per mutation
    let queued = false;
    const queueRetainState = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        retainState();
      });
    };
    new MutationObserver(queueRetainState).observe(grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  }
  // The hero's jump links point at other hub sections; hide any whose section is disabled or removed.
  function hideDeadJumps() {
    document.querySelectorAll('[data-thread-jump]').forEach((link) => {
      link.hidden = !document.getElementById(link.getAttribute('href').slice(1));
    });
  }
  function bindAll() {
    hideDeadJumps();
    bindDefaultSort();
    bindRails();
    bindPagination();
  }
  window.msqcThreadHub = { bindAll };
  bindAll();
  document.addEventListener('DOMContentLoaded', bindAll);
  document.addEventListener('shopify:section:load', bindAll);
  // a section is still in the page while its unload event runs
  document.addEventListener('shopify:section:unload', () => setTimeout(hideDeadJumps));
})();
